import { Trans, useLingui } from '@lingui/react/macro';
import { format } from 'date-fns';
import { MediaKind, mediaSubjectMatchDate } from 'modules/manager/domain/media-kind';
import { MediaSubjectType } from 'modules/manager/domain/media-kind/media-kind-catalog';
import { useChampionshipMediaSubjectsQuery } from 'modules/manager/store/media-generation-jobs-api';
import React, { useMemo, useState } from 'react';
import { UDSelect, SelectOption } from 'ud-form/components/select/component';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';
import UDText from 'ud-ui/components/text';

interface GenerateMediaModalProps {
  championshipId: number;
  kind: MediaKind;
  subjectType: MediaSubjectType;
  kindTitle: string;
  onClose: () => void;
  onSubmit: (subjectId: number) => void;
}

const GenerateMediaModal = ({ championshipId, subjectType, kindTitle, onClose, onSubmit }: GenerateMediaModalProps) => {
  const { t } = useLingui();
  const { data: subjects, isFetching } = useChampionshipMediaSubjectsQuery(championshipId);
  const [selected, setSelected] = useState<SelectOption<number> | null>(null);

  // `isDisabled` — штатное поле опции react-select: такую опцию видно, но выбрать нельзя.
  const options: (SelectOption<number> & { isDisabled?: boolean })[] = useMemo(() => {
    if (!subjects) return [];
    if (subjectType === 'match') {
      return subjects.matches.map((match) => {
        const home = match.teams[0]?.name ?? '—';
        const away = match.teams[1]?.name ?? '—';
        const round = match.round?.name ? `, ${match.round.name}` : '';
        const date = mediaSubjectMatchDate(match.date);
        if (!date) {
          // Анонс без даты печатался с «01.01.1970» (IMGM-1499): такой матч показываем, но не даём выбрать.
          const noDate = t`у матча не задана дата`;
          return { value: match.id, label: `${home} — ${away}${round} · ${noDate}`, isDisabled: true };
        }
        return { value: match.id, label: `${home} — ${away}${round} · ${format(date, 'dd.MM.yyyy HH:mm')}` };
      });
    }
    if (subjectType === 'round') {
      return subjects.rounds.map((round) => ({ value: round.id, label: round.name }));
    }
    if (subjectType === 'stage') {
      return subjects.stages.map((stage) => ({ value: stage.id, label: stage.name }));
    }
    if (subjectType === 'group') {
      return subjects.groups.map((group) => ({ value: group.id, label: group.name }));
    }
    return [];
  }, [subjects, subjectType, t]);

  const handleSubmit = () => {
    if (!selected) return;
    onSubmit(selected.value);
  };

  return (
    <Modal show onClose={onClose} size="md">
      <Modal.Header title={t`Сгенерировать: ${kindTitle}`} />
      <Modal.Body>
        <div data-testid="generate-media-modal">
          {isFetching && !subjects ? (
            <div className="skeleton-block h-12 w-full" />
          ) : options.length === 0 ? (
            <UDText type="subhead" className="text-inkSecondary">
              <Trans>Нет подходящих объектов для этого типа медиа.</Trans>
            </UDText>
          ) : (
            <UDSelect
              placeholder={t`Выберите объект`}
              options={options}
              value={selected}
              onChange={(value) => setSelected(value as SelectOption<number> | null)}
            />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <UDButton variant="secondary" type="button" onClick={onClose}>
          <Trans>Отмена</Trans>
        </UDButton>
        <UDButton variant="primary" type="button" onClick={handleSubmit} disabled={!selected} data-testid="generate-media-submit">
          <Trans>Сгенерировать</Trans>
        </UDButton>
      </Modal.Footer>
    </Modal>
  );
};

export default GenerateMediaModal;
